import type { ContentI18n } from "@/lib/questContent/types";
import type { INegotiationService } from "@/services/interfaces/INegotiationService";
import type {
  PriceProposal,
  NegotiationThread,
  TaskProposalSummary,
  NegotiatingBounty,
} from "@/lib/types";
import { isKnownCurrency } from "@/lib/types";
import { supabase } from "@/lib/supabase";
import { getChainConfig } from "@/config/contracts";
import { callEdgeFunction } from "@/lib/supabaseAuth";
import { TaskAlreadyTakenError, WorkerWalletNotReadyError } from "@/services/errors";
import { negotiationPartyName } from "@/lib/negotiationParty";
import { ACCEPTED_ASKING_PRICE_MESSAGE } from "@/lib/proposalMessage";

// ─── Row mapping ────────────────────────────────────────

/**
 * The shape of a `public_profiles!proposals_worker_id_fkey` embed. Nullable in
 * full: PostgREST answers HTTP 200 with `profiles: null` when the row is not
 * visible, so every consumer must handle absence rather than assume a name.
 */
type PartyProfile = {
  display_name?: string | null;
  handle?: string | null;
  avatar_url?: string | null;
} | null;

interface ProposalRow {
  id: string;
  task_id: string;
  quest_id: string;
  worker_id: string;
  proposed_price: number;
  message: string | null;
  status: string;
  parent_proposal_id: string | null;
  round: number;
  proposed_by: string;
  created_at: string;
  updated_at: string;
}

function mapRow(row: ProposalRow): PriceProposal {
  return {
    id: row.id,
    taskId: row.task_id,
    questId: row.quest_id,
    workerId: row.worker_id,
    proposedPrice: Number(row.proposed_price),
    message: row.message,
    status: row.status as PriceProposal["status"],
    parentProposalId: row.parent_proposal_id,
    round: row.round,
    proposedBy: row.proposed_by as PriceProposal["proposedBy"],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ─── Helpers ────────────────────────────────────────────

function groupByWorker(
  proposals: PriceProposal[],
  taskId: string,
): NegotiationThread[] {
  const byWorker = new Map<string, PriceProposal[]>();
  for (const p of proposals) {
    const arr = byWorker.get(p.workerId) ?? [];
    arr.push(p);
    byWorker.set(p.workerId, arr);
  }

  return Array.from(byWorker.entries()).map(([workerId, workerProposals]) => {
    const sorted = workerProposals.sort((a, b) => a.round - b.round);
    const latest = sorted[sorted.length - 1]!;
    const accepted = sorted.find((p) => p.status === "accepted");
    return {
      taskId,
      workerId,
      // NEVER the worker id: this used to seed `workerName` with the Privy
      // DID, and any caller that failed to enrich it rendered the DID.
      workerName: null, // Resolved by the caller with a public_profiles join
      workerAvatarUrl: null,
      proposals: sorted,
      currentStatus: latest.status,
      latestPrice: latest.proposedPrice,
      agreedPrice: accepted?.proposedPrice ?? null,
    };
  });
}

// ─── Service ────────────────────────────────────────────

/**
 * The database refusals that mean "someone else got there first" rather than
 * a failure: join_workspace_as_accepted_worker's "already awarded" and
 * clone_quest_from_template's "Template is full" / "not open". The UI reads
 * these as TaskAlreadyTakenError, never as message text.
 */
const TAKEN_BY_ANOTHER = /already (been )?awarded|template is (full|not open)/i;

export class SupabaseNegotiationService implements INegotiationService {
  // ─── Worker actions ──────────────────────────────────

  async proposePrice(
    taskId: string,
    questId: string,
    price: number,
    message?: string,
  ): Promise<PriceProposal> {
    const { data, error } = await supabase
      .from("proposals")
      .insert({
        task_id: taskId,
        quest_id: questId,
        proposed_price: price,
        message: message ?? null,
        proposed_by: "worker",
      })
      .select()
      .single();

    if (error) throw new Error(`Failed to propose price: ${error.message}`);
    return mapRow(data as ProposalRow);
  }

  async acceptAskingPrice(
    taskId: string,
    questId: string,
  ): Promise<PriceProposal> {
    // Fetch the task's asking price
    const { data: task, error: taskError } = await supabase
      .from("tasks")
      .select("budget_usd")
      .eq("id", taskId)
      .single();

    if (taskError) throw new Error(`Failed to fetch task: ${taskError.message}`);

    return this.proposePrice(
      taskId,
      questId,
      Number(task.budget_usd),
      ACCEPTED_ASKING_PRICE_MESSAGE,
    );
  }

  async counterPropose(
    proposalId: string,
    price: number,
    message?: string,
  ): Promise<PriceProposal> {
    // Mark parent as countered
    const { data: parent, error: parentError } = await supabase
      .from("proposals")
      .update({ status: "countered", updated_at: new Date().toISOString() })
      .eq("id", proposalId)
      .select()
      .single();

    if (parentError) throw new Error(`Failed to counter: ${parentError.message}`);
    const parentRow = parent as ProposalRow;

    // Insert the new counter-proposal
    const { data, error } = await supabase
      .from("proposals")
      .insert({
        task_id: parentRow.task_id,
        quest_id: parentRow.quest_id,
        worker_id: parentRow.worker_id,
        proposed_price: price,
        message: message ?? null,
        status: "pending",
        parent_proposal_id: proposalId,
        round: parentRow.round + 1,
        proposed_by: "worker",
      })
      .select()
      .single();

    if (error) throw new Error(`Failed to insert counter: ${error.message}`);
    return mapRow(data as ProposalRow);
  }

  async workerAcceptCounterOffer(proposalId: string): Promise<PriceProposal> {
    // Worker directly marks the creator's counter-offer as "accepted".
    // Assignment and budget side effects still happen later, when the CREATOR
    // triggers lockFundsAndStart — RLS requires the quest creator for those
    // task/quest writes.
    //
    // 0. Read the target first — the two preconditions below both need its
    //    task, and the repair path needs the whole row.
    const { data: targetRow, error: lookupErr } = await supabase
      .from("proposals")
      .select("*")
      .eq("id", proposalId)
      .single();
    if (lookupErr) throw new Error(`Failed to load counter-offer: ${lookupErr.message}`);
    const target = targetRow as ProposalRow;

    // 0-listing. A $0 invitation on a stock listing (is_template_anchor) must
    //     NOT be accepted in place: that hands the listing's one anchor task to
    //     the first volunteer, never counts a slot, and the sibling-supersede
    //     trigger kills every other invitation. It joins the way the creator's
    //     Applicants accept does — clone_quest_from_template mints this
    //     worker's own clone, moves the proposal onto it, and seats both
    //     parties in its workspace. 20260922150000 lets the invitee call it for
    //     exactly this proposal shape. The returned proposal carries the
    //     CLONE's quest id, which is how the caller knows to navigate there.
    //     (No clone-translation kick: translate-content is creator/admin only.
    //     The clone inherits the anchor's translation inside the RPC —
    //     20260930150000 — and the sweep backfills anything that copy missed.)
    if (
      target.proposed_by === "creator" &&
      Number(target.proposed_price) === 0 &&
      target.status === "pending"
    ) {
      const { data: questRow, error: questErr } = await supabase
        .from("quests")
        .select("is_template_anchor, template_id")
        .eq("id", target.quest_id)
        .maybeSingle();
      if (questErr) throw new Error(`Failed to load quest: ${questErr.message}`);
      const listing = questRow as { is_template_anchor: boolean; template_id: string | null } | null;
      if (listing?.is_template_anchor && listing.template_id) {
        const { error: cloneErr } = await supabase.rpc("clone_quest_from_template", {
          p_template_id: listing.template_id,
          p_worker_id: target.worker_id,
          p_proposal_id: proposalId,
          p_proposed_price: 0,
        });
        if (cloneErr) {
          const reason = `Failed to accept counter-offer: ${cloneErr.message}`;
          throw TAKEN_BY_ANOTHER.test(cloneErr.message) ? new TaskAlreadyTakenError(reason) : new Error(reason);
        }
        const { data: moved, error: movedErr } = await supabase
          .from("proposals")
          .select("*")
          .eq("id", proposalId)
          .single();
        if (movedErr) throw new Error(`Failed to load accepted invitation: ${movedErr.message}`);
        return mapRow(moved as ProposalRow);
      }
    }

    // 0a. A task holds exactly one worker. `acceptProposal` enforces that by
    //     superseding rivals, but this door never did — and unlike the
    //     creator's door it cannot, because RLS only lets a worker write
    //     their own proposals. So refuse instead: `can_read_task_thread`
    //     (20260916100000) grants on an accepted proposal, so a second
    //     acceptance would put two workers in one task thread, each able to
    //     read the other's messages, identity and price. That is the
    //     constraint this feature must never break.
    const { data: rivals, error: rivalErr } = await supabase
      .from("proposals")
      .select("id")
      .eq("task_id", target.task_id)
      .eq("status", "accepted")
      .neq("worker_id", target.worker_id);
    if (rivalErr) throw new Error(`Failed to check task availability: ${rivalErr.message}`);
    if (rivals && rivals.length > 0) {
      throw new TaskAlreadyTakenError("This task has already been awarded to another worker.");
    }

    // 1. Flip to accepted — but only FROM pending. `accepted` is terminal in
    //    enforce_proposal_status_transition (20260403000005), so an
    //    unconditional update makes a retry raise instead of repairing. With
    //    this filter a retry matches zero rows and falls through to the
    //    membership write below, which is the step that actually needs
    //    repairing when it failed the first time.
    const { data, error } = await supabase
      .from("proposals")
      .update({ status: "accepted", updated_at: new Date().toISOString() })
      .eq("id", proposalId)
      .eq("status", "pending")
      .select()
      .maybeSingle();
    if (error) throw new Error(`Failed to accept counter-offer: ${error.message}`);

    if (!data && target.status !== "accepted") {
      // Not pending and not already ours — withdrawn, superseded, rejected,
      // or someone else's row that RLS refused to match.
      const reason = `Failed to accept counter-offer: no longer open (${target.status}).`;
      // Superseded = a sibling was accepted first (close_sibling_volunteer_invites).
      throw target.status === "superseded" ? new TaskAlreadyTakenError(reason) : new Error(reason);
    }
    const accepted = mapRow((data ?? target) as ProposalRow);

    // Workspace membership is the exception, and it opens now rather than at
    // funding: on a multi-task quest the worker may wait a long time for a
    // sibling task to fill, and silence for that whole period is the main cost
    // of all-or-nothing funding.
    //
    // This goes through an RPC rather than a direct insert. `workspace_members_insert`
    // (20260916140000) requires `tasks.assignee_id = requesting_user_id()`, and
    // assignee_id is a CREATOR write that happens later — so at accept time the
    // worker is not yet the assignee and a direct insert is refused. The RPC
    // keys on the one fact a worker cannot forge: a proposal the CREATOR
    // authored (`proposed_by = 'creator'`) and this worker accepted. See the
    // migration header for why the policy was not loosened instead.
    const { error: joinErr } = await supabase.rpc("join_workspace_as_accepted_worker", {
      p_quest_id: accepted.questId,
    });
    if (joinErr) {
      const reason = `Failed to open the workspace thread: ${joinErr.message}`;
      throw TAKEN_BY_ANOTHER.test(joinErr.message) ? new TaskAlreadyTakenError(reason) : new Error(reason);
    }

    return accepted;
  }

  /**
   * Put a worker in the quest's workspace so their chat thread exists.
   *
   * ignoreDuplicates → ON CONFLICT DO NOTHING: `workspace_members` has no
   * UPDATE policy, so the UPDATE branch of a real upsert would be refused by
   * RLS. That also makes the write safe to repeat, which matters because
   * `lockFundsAndStart` issues the same upsert later as a backstop for quests
   * whose membership predates accept-time creation.
   */
  private async addWorkerToWorkspace(questId: string, workerId: string): Promise<void> {
    const { error } = await supabase
      .from("workspace_members")
      .upsert(
        { quest_id: questId, user_id: workerId, role: "worker" },
        { onConflict: "quest_id,user_id", ignoreDuplicates: true },
      );
    if (error) throw new Error(`Failed to add worker to workspace: ${error.message}`);
  }

  async withdrawProposal(proposalId: string): Promise<void> {
    // `status = pending` + the returned row: an UPDATE that RLS or a stale id
    // filters to zero rows is not an error to PostgREST, and without these a
    // withdraw of an already-countered round "succeeded" while the creator's
    // live counter stayed open.
    const { data, error } = await supabase
      .from("proposals")
      .update({ status: "withdrawn", updated_at: new Date().toISOString() })
      .eq("id", proposalId)
      .eq("status", "pending")
      .select("id");

    if (error) throw new Error(`Failed to withdraw: ${error.message}`);
    if (!data || data.length === 0) {
      throw new Error("Failed to withdraw: the proposal is no longer pending");
    }
  }

  // ─── Creator actions ─────────────────────────────────

  async acceptProposal(proposalId: string): Promise<PriceProposal> {
    // 0. Precondition — verify the worker has finished wallet onboarding
    //    BEFORE writing any state. profiles.wallet_address is briefly
    //    NULL between usePrivySync's initial profile insert and the
    //    wallet-address re-sync. If we proceed past this point with a
    //    null wallet, the downstream lockFundsAndStart on-chain assign
    //    will throw, leaving tasks/proposals/workspace_members in a
    //    half-accepted state that requires manual cleanup. Failing
    //    here keeps the operation atomic and lets the UI render a
    //    "waiting on worker onboarding" state.
    //
    //    DEV-246: this precondition exists only to protect the on-chain assign.
    //    A volunteer ($0) proposal never reaches that call — lockFundsAndStart
    //    returns before any chain work — so gating it on the worker's embedded
    //    wallet would block the volunteer flow for no benefit. Skip it there.
    const { data: proposalLookup, error: lookupErr } = await supabase
      .from("proposals")
      .select(
        "worker_id, proposed_price, profiles:public_profiles!proposals_worker_id_fkey(display_name, handle)",
      )
      .eq("id", proposalId)
      .single();
    if (lookupErr) {
      throw new Error(`Failed to load proposal: ${lookupErr.message}`);
    }
    const workerId = (proposalLookup as { worker_id: string }).worker_id;
    const workerProfile = (
      proposalLookup as {
        profiles?: { display_name?: string | null; handle?: string | null } | null;
      }
    ).profiles;
    const workerName =
      negotiationPartyName({
        displayName: workerProfile?.display_name,
        handle: workerProfile?.handle,
      }) ?? undefined;

    const isVolunteerOffer =
      Number((proposalLookup as { proposed_price: number }).proposed_price) === 0;

    const { data: workerWallet, error: walletErr } = isVolunteerOffer
      ? { data: null, error: null }
      : await supabase.rpc("get_user_wallet", { p_user_id: workerId });
    if (walletErr) {
      throw new Error(`Failed to verify worker wallet: ${walletErr.message}`);
    }
    if (!isVolunteerOffer && !workerWallet) {
      // Server-side fallback: ask the sync-worker-wallet edge function to
      // fetch the embedded EOA from Privy directly and persist it. Covers
      // legacy NULLs from before the worker-side gate landed and the case
      // where the worker's tab never finished usePrivySync.
      const synced = await callEdgeFunction<{ wallet_address: string | null }>(
        "sync-worker-wallet",
        { body: { worker_id: workerId } },
      ).catch(() => null);
      if (!synced?.wallet_address) {
        throw new WorkerWalletNotReadyError(workerId, workerName);
      }
    }

    // 1. Accept the proposal
    const { data, error } = await supabase
      .from("proposals")
      .update({ status: "accepted", updated_at: new Date().toISOString() })
      .eq("id", proposalId)
      .select()
      .single();
    if (error) throw new Error(`Failed to accept proposal: ${error.message}`);
    const accepted = mapRow(data as ProposalRow);

    // 2. Supersede other pending proposals on this task
    const { error: supersedeErr } = await supabase
      .from("proposals")
      .update({ status: "superseded", updated_at: new Date().toISOString() })
      .eq("task_id", accepted.taskId)
      .eq("status", "pending")
      .neq("worker_id", accepted.workerId);
    if (supersedeErr) throw new Error(`Failed to supersede proposals: ${supersedeErr.message}`);

    // 3. Assign worker to this task and update budget to agreed price
    const { error: taskErr } = await supabase
      .from("tasks")
      .update({
        assignee_id: accepted.workerId,
        budget_usd: accepted.proposedPrice,
        status: "assigned",
      })
      .eq("id", accepted.taskId);
    if (taskErr) throw new Error(`Failed to assign worker to task: ${taskErr.message}`);

    // 4. Recalculate quest total_budget_usd from all tasks' current budgets
    const { data: allTasks, error: tasksErr } = await supabase
      .from("tasks")
      .select("budget_usd")
      .eq("quest_id", accepted.questId);
    if (tasksErr) throw new Error(`Failed to fetch tasks for budget calc: ${tasksErr.message}`);

    const totalBudget = (allTasks ?? []).reduce(
      (sum, t) => sum + Number(t.budget_usd), 0,
    );

    // Update quest budget but keep status as "published" — it only becomes
    // "active" after lockFundsAndStart completes the on-chain flow.
    const { error: questErr } = await supabase
      .from("quests")
      .update({ total_budget_usd: totalBudget })
      .eq("id", accepted.questId);
    if (questErr) throw new Error(`Failed to update quest budget: ${questErr.message}`);

    // 5. Add worker to workspace.
    await this.addWorkerToWorkspace(accepted.questId, accepted.workerId);

    // 6. System message
    const { error: msgErr } = await supabase.from("workspace_messages").insert({
      quest_id: accepted.questId,
      sender_id: null,
      sender_role: "system",
      content: `💰 Price agreed at $${accepted.proposedPrice} for this task. Worker assigned.`,
      message_type: "system",
    });
    if (msgErr) throw new Error(`Failed to send system message: ${msgErr.message}`);

    return accepted;
  }

  async rejectProposal(proposalId: string): Promise<void> {
    const { error } = await supabase
      .from("proposals")
      .update({ status: "rejected", updated_at: new Date().toISOString() })
      .eq("id", proposalId);

    if (error) throw new Error(`Failed to reject: ${error.message}`);
  }

  async creatorCounterPropose(
    proposalId: string,
    price: number,
    message?: string,
  ): Promise<PriceProposal> {
    // Mark parent as countered
    const { data: parent, error: parentError } = await supabase
      .from("proposals")
      .update({ status: "countered", updated_at: new Date().toISOString() })
      .eq("id", proposalId)
      .select()
      .single();

    if (parentError) throw new Error(`Failed to counter: ${parentError.message}`);
    const parentRow = parent as ProposalRow;

    // Insert the creator's counter-proposal
    const { data, error } = await supabase
      .from("proposals")
      .insert({
        task_id: parentRow.task_id,
        quest_id: parentRow.quest_id,
        worker_id: parentRow.worker_id,
        proposed_price: price,
        message: message ?? null,
        status: "pending",
        parent_proposal_id: proposalId,
        round: parentRow.round + 1,
        proposed_by: "creator",
      })
      .select()
      .single();

    if (error) throw new Error(`Failed to insert counter: ${error.message}`);
    return mapRow(data as ProposalRow);
  }

  // ─── Queries ─────────────────────────────────────────

  async getProposalsForTask(taskId: string): Promise<NegotiationThread[]> {
    const { data, error } = await supabase
      .from("proposals")
      .select(
        "*, profiles:public_profiles!proposals_worker_id_fkey(display_name, handle, avatar_url)",
      )
      .eq("task_id", taskId)
      .order("round", { ascending: true });

    if (error) throw new Error(`Failed to fetch proposals: ${error.message}`);

    const rows = data as (ProposalRow & { profiles: PartyProfile })[];
    const proposals = rows.map((row) => mapRow(row));

    const threads = groupByWorker(proposals, taskId);

    // Enrich worker names from joined profile data
    const profileMap = new Map<string, { name: string | null; avatar: string | null }>();
    for (const row of rows) {
      if (row.profiles && !profileMap.has(row.worker_id)) {
        profileMap.set(row.worker_id, {
          name: negotiationPartyName({
            displayName: row.profiles.display_name,
            handle: row.profiles.handle,
          }),
          avatar: row.profiles.avatar_url ?? null,
        });
      }
    }

    return threads.map((t) => {
      const profile = profileMap.get(t.workerId);
      return {
        ...t,
        // No `?? t.workerId`. An unreadable profile yields null and the UI
        // renders a translated generic — it must never render the Privy DID.
        workerName: profile?.name ?? null,
        workerAvatarUrl: profile?.avatar ?? null,
      };
    });
  }

  async getMyProposalForTask(
    taskId: string,
    workerId: string,
  ): Promise<NegotiationThread | null> {
    // Filter on worker_id explicitly. RLS narrows a plain worker to their own
    // rows, but admin_select_all_proposals and proposals_select_quest_creator
    // let admins and creators read everyone's — without this, an admin was
    // shown another worker's proposal as their own application.
    const { data, error } = await supabase
      .from("proposals")
      .select("*")
      .eq("task_id", taskId)
      .eq("worker_id", workerId)
      .order("round", { ascending: true });

    if (error) throw new Error(`Failed to fetch my proposals: ${error.message}`);
    if (!data || data.length === 0) return null;

    const proposals = (data as ProposalRow[]).map(mapRow);
    const threads = groupByWorker(proposals, taskId);
    return threads[0] ?? null;
  }

  async getProposalsSummary(
    questId: string,
  ): Promise<TaskProposalSummary[]> {
    // Driven from TASKS, not from proposals.
    //
    // Grouping proposals by task meant a task nobody had proposed on produced
    // no row at all, and ProposalCard funds escrow on
    //
    //     summaries.length > 0 && summaries.every((s) => s.agreedPrice !== null)
    //
    // `every()` is vacuously true over a task that is not in the array, so a
    // 3-task quest with 1 accepted proposal reported itself fully agreed,
    // locked funds on-chain and left two tasks unstaffed. The only thing that
    // kept it from firing is the `tasks.length > 1` guard at the four creation
    // doors, which Task 3.4 removes — so this has to be right first.
    //
    // Cancelled tasks are excluded rather than counted-and-ignored: a task the
    // creator dropped (Task 3.3) must not hold escrow hostage forever, and
    // making that the SERVER's filter keeps the rule in one place.
    const { data, error } = await supabase
      .from("tasks")
      .select(
        "id, title, budget_usd, status, i18n, source_lang, proposals(*, profiles:public_profiles!proposals_worker_id_fkey(display_name, handle))",
      )
      .eq("quest_id", questId)
      .neq("status", "cancelled")
      .order("order", { ascending: true });

    if (error) throw new Error(`Failed to fetch summary: ${error.message}`);

    type SummaryTaskRow = {
      id: string;
      title: string | null;
      budget_usd: number | null;
      status: string;
      i18n: ContentI18n | null;
      source_lang: string | null;
      proposals: (ProposalRow & { profiles: PartyProfile })[] | null;
    };

    return ((data ?? []) as SummaryTaskRow[]).map((task) => {
      const proposals = task.proposals ?? [];
      const accepted = proposals.find((p) => p.status === "accepted");
      const pending = proposals.filter((p) => p.status === "pending");
      // Distinct WORKERS, not rows: a negotiation is several rounds by the
      // same person, and counting rows would report one haggler as a crowd.
      const uniqueWorkers = new Set(proposals.map((p) => p.worker_id));

      return {
        taskId: task.id,
        // The task's own title and budget, which is the point: previously
        // these were read off the first proposal's embedded task, so a task
        // with no proposals had nowhere to read them from at all.
        taskTitle: task.title ?? task.id,
        askingPrice: Number(task.budget_usd ?? 0),
        totalProposals: uniqueWorkers.size,
        pendingProposals: pending.length,
        acceptedWorkerId: accepted?.worker_id ?? null,
        acceptedWorkerName: negotiationPartyName({
          displayName: accepted?.profiles?.display_name,
          handle: accepted?.profiles?.handle,
        }),
        agreedPrice: accepted ? Number(accepted.proposed_price) : null,
        i18n: task.i18n ?? null,
        sourceLang: task.source_lang ?? null,
        // The server's funding predicate, computed once here so no client has
        // to guess it. lockFundsAndStart exempts a $0 task because escrow
        // never has to cover it; a client checking `agreedPrice !== null`
        // instead would hold the whole quest hostage to a volunteer task.
        isStaffed: accepted !== undefined || Number(task.budget_usd ?? 0) <= 0,
      };
    });
  }

  async getMyActiveNegotiations(workerId: string): Promise<NegotiatingBounty[]> {
    // Scoped to the active network, like every other list on /work.
    // `getMyInterests` and `getMyBounties` have always filtered on
    // quests.chain_id; this one never did, so testnet rows showed up beside
    // mainnet ones under Negotiating. That was survivable while the rows were
    // confined to their own chip — but the unanswered half of this result now
    // feeds the Interested list AND its count (see myBountyLists.mergeInterested),
    // so the leak reached a second surface. `!inner` on both embeds is what
    // makes the filter a real restriction rather than a null-out.
    const chainId = getChainConfig().chainId;
    const { data, error } = await supabase
      .from("proposals")
      .select(
        `*, tasks:task_id!inner(title, status, task_type, budget_usd, quest_id, i18n, source_lang, quests:quest_id!inner(title, status, chain_id, currency, i18n, source_lang))`,
      )
      // Every status, not just the live ones: the latest round decides. A
      // live-only filter would drop a rejected round 3 and promote the
      // countered round 2 to "latest", reviving a closed negotiation forever.
      .eq("worker_id", workerId)
      .eq("tasks.quests.chain_id", chainId)
      .order("updated_at", { ascending: false });

    if (error) throw new Error(`Failed to fetch negotiations: ${error.message}`);
    if (!data || data.length === 0) return [];

    type NegRow = ProposalRow & {
      tasks: {
        title: string;
        status: string;
        task_type: string;
        budget_usd: number;
        quest_id: string;
        /** DEV-824: the task's own translations. */
        i18n: ContentI18n | null;
        source_lang: string | null;
        quests: {
          title: string;
          status: string;
          chain_id: number;
          currency: string;
          i18n: ContentI18n | null;
          source_lang: string | null;
        };
      } | null;
    };

    // Group by task — take the latest proposal per task
    const byTask = new Map<string, NegRow>();
    for (const row of data as NegRow[]) {
      const existing = byTask.get(row.task_id);
      if (!existing || row.round > existing.round) {
        byTask.set(row.task_id, row);
      }
    }

    const LIVE = new Set(["pending", "countered", "accepted"]);
    // An unanswered offer on work that is over can never be answered. Since
    // 20261015110000 the DB retires such proposals when the quest or task
    // finishes (triggers on quests/tasks, same two status sets as below), so
    // this is a second layer: it covers a client running against a database
    // without that migration and any row a future path forgets. Accepted
    // rows stay — that's the worker's own hire, whatever its state.
    const OVER_TASK = new Set(["completed", "approved", "cancelled"]);
    const OVER_QUEST = new Set(["completed", "closed", "cancelled"]);
    const stillOpen = (row: NegRow) =>
      row.status === "accepted" ||
      (!OVER_TASK.has(row.tasks!.status) && !OVER_QUEST.has(row.tasks!.quests.status));
    return Array.from(byTask.values())
      .filter((row) => row.tasks != null && LIVE.has(row.status) && stillOpen(row))
      .map((row) => {
        const proposal = mapRow(row);
        let negotiationStatus: NegotiatingBounty["negotiationStatus"];
        if (proposal.status === "accepted") {
          negotiationStatus = "accepted";
        } else if (
          proposal.status === "pending" &&
          proposal.proposedBy === "creator"
        ) {
          negotiationStatus = "counter_received";
        } else {
          negotiationStatus = "awaiting_response";
        }

        return {
          taskId: row.task_id,
          questId: row.quest_id,
          taskTitle: row.tasks!.title,
          questTitle: row.tasks!.quests.title,
          taskType: row.tasks!.task_type as NegotiatingBounty["taskType"],
          budgetUsd: Number(row.tasks!.budget_usd),
          currency: isKnownCurrency(row.tasks!.quests.currency)
            ? row.tasks!.quests.currency
            : "USDT",
          myLatestProposal: proposal,
          negotiationStatus,
          i18n: row.tasks!.i18n ?? null,
          sourceLang: row.tasks!.source_lang ?? null,
          questI18n: row.tasks!.quests.i18n ?? null,
          questSourceLang: row.tasks!.quests.source_lang ?? null,
        };
      });
  }

  // ─── Real-time ───────────────────────────────────────

  subscribeToProposals(
    _taskId: string,
    _callback: (proposal: PriceProposal) => void,
  ): () => void {
    // No-op: Supabase Realtime is disabled due to Privy token-exchange auth
    // causing WebSocket reconnect storms. Consuming queries (useTaskProposals,
    // useMyProposal) already poll at 5s via refetchInterval.
    return () => {};
  }
}
